/**
 * ChannelPostsTab — B-2026-0074
 *
 * Renders the Posts tab for ChannelPage:
 *   - Owner-only "New Post" compose area (Task 8)
 *   - Post list: pinned posts at top (sticky-card style), then chronological (Task 7)
 *   - Each post card: author name + role badge, post_type icon, body (expandable if >200 chars),
 *     timestamp, owner-only edit/delete actions
 *   - Soft-deleted posts render as a placeholder, not removed
 */

import { useState, useEffect, useCallback } from 'react';
import {
  MegaphoneIcon,
  ArrowPathIcon,
  SparklesIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  listChannelPosts,
  createChannelPost,
  editChannelPost,
  deleteChannelPost,
} from '@/services/channelPostsApi';
import type { ChannelPostResponse, PostType } from '@/services/channelPostsApi';
import Spinner from '@/components/ui/Spinner';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_BODY_LEN = 5000;
const TRUNCATE_AT = 200;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(isoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Map post_type to icon + colour accent. */
function postTypeConfig(postType: string): { icon: React.ReactNode; label: string; color: string; bg: string } {
  switch (postType) {
    case 'announcement':
      return {
        icon: <MegaphoneIcon style={{ width: '13px', height: '13px' }} />,
        label: 'Announcement',
        color: 'var(--color-accent)',
        bg: 'rgba(30,215,96,0.12)',
      };
    case 'milestone':
      return {
        icon: <SparklesIcon style={{ width: '13px', height: '13px' }} />,
        label: 'Milestone',
        color: '#F5C542',
        bg: 'rgba(245,197,66,0.12)',
      };
    case 'update':
    default:
      return {
        icon: <ArrowPathIcon style={{ width: '13px', height: '13px' }} />,
        label: 'Update',
        color: 'var(--color-accent-blue)',
        bg: 'rgba(61,169,252,0.12)',
      };
  }
}

/** Role badge style — mirrors CommentItem pattern. */
function RoleBadge({ role }: { role: string }) {
  const styles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 7px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'capitalize' as const,
  };
  if (role === 'admin') {
    return (
      <span style={{ ...styles, backgroundColor: 'rgba(239,68,68,0.12)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}>
        {role}
      </span>
    );
  }
  if (role === 'coach') {
    return (
      <span style={{ ...styles, backgroundColor: 'rgba(61,169,252,0.12)', color: 'var(--color-accent-blue)', border: '1px solid rgba(61,169,252,0.22)' }}>
        {role}
      </span>
    );
  }
  return (
    <span style={{ ...styles, backgroundColor: 'var(--color-dark-elevated-2)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-subtle)' }}>
      {role}
    </span>
  );
}

// ─── PostCard ─────────────────────────────────────────────────────────────────

interface PostCardProps {
  post: ChannelPostResponse;
  isOwner: boolean;
  slug: string;
  onUpdate: (updated: ChannelPostResponse) => void;
}

function PostCard({ post, isOwner, slug, onUpdate }: PostCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(post.body);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { icon, label, color, bg } = postTypeConfig(post.post_type);
  const isTruncatable = !post.deleted && post.body.length > TRUNCATE_AT;
  const displayBody = isTruncatable && !isExpanded
    ? post.body.slice(0, TRUNCATE_AT) + '…'
    : post.body;

  const handleSaveEdit = async () => {
    const trimmed = editBody.trim();
    if (!trimmed || trimmed === post.body) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      const updated = await editChannelPost(slug, post.id, { body: trimmed });
      onUpdate(updated);
      setIsEditing(false);
    } catch {
      // Error stays silent — the form stays open so user can retry.
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePin = async () => {
    try {
      const updated = await editChannelPost(slug, post.id, { pinned: !post.pinned });
      onUpdate(updated);
    } catch {
      // best-effort
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post? It will be shown as a placeholder to preserve thread numbering.')) return;
    setIsDeleting(true);
    try {
      const updated = await deleteChannelPost(slug, post.id);
      onUpdate(updated);
    } finally {
      setIsDeleting(false);
    }
  };

  // Soft-deleted placeholder
  if (post.deleted) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '14px',
          padding: '16px 20px',
          opacity: 0.55,
        }}
      >
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
          This post was deleted.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: post.pinned
          ? `1px solid rgba(30,215,96,0.28)`
          : '1px solid var(--color-border-subtle)',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: post.pinned ? '0 0 0 1px rgba(30,215,96,0.10)' : 'var(--shadow-card)',
        transition: 'border-color 150ms ease',
      }}
    >
      {/* Pinned banner */}
      {post.pinned && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 16px',
            backgroundColor: 'rgba(30,215,96,0.07)',
            borderBottom: '1px solid rgba(30,215,96,0.14)',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--color-accent)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          }}
        >
          <MapPinIcon style={{ width: '12px', height: '12px' }} />
          Pinned
        </div>
      )}

      <div style={{ padding: '16px 20px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', flex: 1, minWidth: 0 }}>
            {/* Author */}
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {post.author_display_name ?? 'Unknown'}
            </span>
            {/* Role badge */}
            {post.author_role && <RoleBadge role={post.author_role} />}
            {/* Post type badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 8px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 600,
                color,
                backgroundColor: bg,
                border: `1px solid ${color}30`,
              }}
            >
              {icon}
              {label}
            </span>
            {/* Edited indicator */}
            {post.edited_at && (
              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>(edited)</span>
            )}
          </div>

          {/* Timestamp + owner actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
              {formatRelativeTime(post.created_at)}
            </span>
            {isOwner && !isEditing && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Toggle pin */}
                <button
                  type="button"
                  onClick={handleTogglePin}
                  title={post.pinned ? 'Unpin post' : 'Pin post'}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '6px',
                    color: post.pinned ? 'var(--color-accent)' : 'var(--color-text-muted)',
                    transition: 'color 120ms ease, background-color 120ms ease',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                >
                  <MapPinIcon style={{ width: '14px', height: '14px' }} />
                </button>
                {/* Edit */}
                <button
                  type="button"
                  onClick={() => { setIsEditing(true); setEditBody(post.body); }}
                  title="Edit post"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '6px',
                    color: 'var(--color-text-muted)',
                    transition: 'color 120ms ease, background-color 120ms ease',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)'; }}
                >
                  <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                </button>
                {/* Delete */}
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={isDeleting}
                  title="Delete post"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    padding: '4px',
                    borderRadius: '6px',
                    color: 'var(--color-text-muted)',
                    opacity: isDeleting ? 0.4 : 1,
                    transition: 'color 120ms ease, background-color 120ms ease',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => { if (!isDeleting) { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239,68,68,0.10)'; (e.currentTarget as HTMLButtonElement).style.color = '#EF4444'; } }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)'; }}
                >
                  <TrashIcon style={{ width: '14px', height: '14px' }} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        {isEditing ? (
          <div>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              maxLength={MAX_BODY_LEN}
              rows={4}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                resize: 'vertical',
                backgroundColor: 'var(--color-dark-elevated-1)',
                border: '1px solid var(--color-border-strong)',
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '14px',
                color: 'var(--color-text-primary)',
                lineHeight: 1.6,
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'var(--color-accent)'; }}
              onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'var(--color-border-strong)'; }}
              autoFocus
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                {editBody.length}/{MAX_BODY_LEN}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => { setIsEditing(false); setEditBody(post.body); }}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--color-dark-elevated-2)',
                    border: '1px solid var(--color-border-strong)',
                    color: 'var(--color-text-secondary)',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveEdit()}
                  disabled={isSaving || !editBody.trim()}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--color-accent)',
                    border: 'none',
                    color: 'var(--color-text-inverse)',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: isSaving || !editBody.trim() ? 'not-allowed' : 'pointer',
                    opacity: isSaving || !editBody.trim() ? 0.5 : 1,
                  }}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.7,
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {displayBody}
            </p>
            {isTruncatable && (
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                style={{
                  marginTop: '8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--color-accent)',
                  padding: 0,
                }}
              >
                {isExpanded ? (
                  <>Show less <ChevronUpIcon style={{ width: '13px', height: '13px' }} /></>
                ) : (
                  <>Read more <ChevronDownIcon style={{ width: '13px', height: '13px' }} /></>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── ComposePost ──────────────────────────────────────────────────────────────

interface ComposePostProps {
  slug: string;
  onCreated: (post: ChannelPostResponse) => void;
}

function ComposePost({ slug, onCreated }: ComposePostProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [body, setBody] = useState('');
  const [postType, setPostType] = useState<PostType>('announcement');
  const [pinned, setPinned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState(false);

  const charRemaining = MAX_BODY_LEN - body.length;
  const isOverLimit = charRemaining < 0;
  const canSubmit = body.trim().length > 0 && !isOverLimit && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const post = await createChannelPost(slug, { body: body.trim(), post_type: postType, pinned });
      onCreated(post);
      setBody('');
      setPostType('announcement');
      setPinned(false);
      setIsOpen(false);
      setSuccessMsg(true);
      setTimeout(() => setSuccessMsg(false), 3000);
    } catch {
      setError('Could not publish post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Collapsed trigger */}
      {!isOpen ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            padding: '10px 20px',
            borderRadius: '999px',
            backgroundColor: 'var(--color-accent)',
            border: 'none',
            color: 'var(--color-text-inverse)',
            fontSize: '14px',
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color 150ms ease, box-shadow 150ms ease',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 18px rgba(30,215,96,0.35)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
        >
          <PlusIcon style={{ width: '15px', height: '15px' }} />
          New Post
        </button>
      ) : (
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <h3
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: '15px',
              color: 'var(--color-text-primary)',
              margin: '0 0 16px',
            }}
          >
            New Post
          </h3>

          {/* Post type selector */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {(['announcement', 'update', 'milestone'] as PostType[]).map((type) => {
              const cfg = postTypeConfig(type);
              const isSelected = postType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setPostType(type)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: isSelected ? `1px solid ${cfg.color}60` : '1px solid var(--color-border-subtle)',
                    backgroundColor: isSelected ? cfg.bg : 'var(--color-dark-elevated-1)',
                    color: isSelected ? cfg.color : 'var(--color-text-muted)',
                    transition: 'all 120ms ease',
                  }}
                >
                  {cfg.icon}
                  {cfg.label}
                </button>
              );
            })}
          </div>

          {/* Textarea */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your post here…"
            rows={5}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              backgroundColor: 'var(--color-dark-elevated-1)',
              border: `1px solid ${isOverLimit ? '#EF4444' : 'var(--color-border-strong)'}`,
              borderRadius: '10px',
              padding: '12px 14px',
              fontSize: '14px',
              color: 'var(--color-text-primary)',
              lineHeight: 1.6,
              outline: 'none',
              fontFamily: 'inherit',
              marginBottom: '10px',
            }}
            onFocus={(e) => { if (!isOverLimit) (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'var(--color-accent)'; }}
            onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = isOverLimit ? '#EF4444' : 'var(--color-border-strong)'; }}
          />

          {/* Character counter + pin toggle row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
            <span
              style={{
                fontSize: '12px',
                color: isOverLimit ? '#EF4444' : charRemaining <= 200 ? 'var(--color-accent-gold)' : 'var(--color-text-muted)',
                fontWeight: charRemaining <= 100 ? 600 : 400,
              }}
            >
              {charRemaining < 0 ? `${Math.abs(charRemaining)} over limit` : `${charRemaining} characters remaining`}
            </span>
            {/* Pin toggle */}
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: pinned ? 'var(--color-accent)' : 'var(--color-text-muted)',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                style={{ accentColor: 'var(--color-accent)', width: '14px', height: '14px' }}
              />
              <MapPinIcon style={{ width: '13px', height: '13px' }} />
              Pin to top
            </label>
          </div>

          {/* Error */}
          {error && (
            <p style={{ fontSize: '13px', color: '#EF4444', marginBottom: '10px' }}>{error}</p>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => { setIsOpen(false); setBody(''); setError(null); }}
              style={{
                padding: '8px 18px',
                borderRadius: '999px',
                backgroundColor: 'var(--color-dark-elevated-2)',
                border: '1px solid var(--color-border-strong)',
                color: 'var(--color-text-secondary)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
              style={{
                padding: '8px 20px',
                borderRadius: '999px',
                backgroundColor: 'var(--color-accent)',
                border: 'none',
                color: 'var(--color-text-inverse)',
                fontSize: '13px',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: canSubmit ? 1 : 0.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isSubmitting && (
                <span style={{ width: '13px', height: '13px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: 'rgba(0,0,0,0.7)', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              )}
              {isSubmitting ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
      )}

      {/* Success toast */}
      {successMsg && (
        <div
          style={{
            marginTop: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '10px',
            backgroundColor: 'rgba(30,215,96,0.10)',
            border: '1px solid rgba(30,215,96,0.25)',
            color: 'var(--color-accent)',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <CheckCircleIcon style={{ width: '15px', height: '15px' }} />
          Post published!
        </div>
      )}
    </div>
  );
}

// ─── ChannelPostsTab ──────────────────────────────────────────────────────────

interface ChannelPostsTabProps {
  slug: string;
  isOwner: boolean;
}

export function ChannelPostsTab({ slug, isOwner }: ChannelPostsTabProps) {
  const [posts, setPosts] = useState<ChannelPostResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPosts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listChannelPosts(slug);
      setPosts(data);
    } catch {
      setError('Could not load posts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  const handleCreated = (post: ChannelPostResponse) => {
    setPosts((prev) => {
      // Insert pinned at front, non-pinned after last pinned item
      if (post.pinned) {
        return [post, ...prev];
      }
      const lastPinnedIdx = prev.reduce((acc, p, i) => (p.pinned ? i : acc), -1);
      const insertAt = lastPinnedIdx + 1;
      const next = [...prev];
      next.splice(insertAt, 0, post);
      return next;
    });
  };

  const handleUpdate = (updated: ChannelPostResponse) => {
    setPosts((prev) => {
      const updated_posts = prev.map((p) => (p.id === updated.id ? updated : p));
      // Re-sort: pinned first, then by created_at desc
      return [...updated_posts].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    });
  };

  return (
    <div>
      {/* Compose area — owner only */}
      {isOwner && <ComposePost slug={slug} onCreated={handleCreated} />}

      {/* Post list */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Spinner size="lg" label="Loading posts…" />
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '32px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
          <p>{error}</p>
          <button
            type="button"
            onClick={() => void loadPosts()}
            style={{
              marginTop: '12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-accent)',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            Retry
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px dashed var(--color-border-strong)',
            borderRadius: '20px',
            padding: '56px 32px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              backgroundColor: 'var(--color-dark-elevated-2)',
              border: '1px solid var(--color-border-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <MegaphoneIcon style={{ width: '28px', height: '28px', color: 'var(--color-text-muted)' }} />
          </div>
          <h3
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: '16px',
              color: 'var(--color-text-primary)',
              margin: '0 0 8px',
            }}
          >
            No posts yet
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.6, maxWidth: '320px', margin: '0 auto' }}>
            {isOwner
              ? 'Share an announcement, update, or milestone with your channel members.'
              : 'The coach hasn\'t posted anything yet. Check back soon!'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              isOwner={isOwner}
              slug={slug}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
